/**
 * Binary codecs for the two hot, unreliable messages (research 04 §4.6):
 * owner state (one per player, 10–20 Hz) and host monster snapshots.
 * Positions are quantised to 1/32 m in int16 (±1024 m), yaw to one byte.
 */

export const MSG_OWNER = 0x10;
export const MSG_MONSTERS = 0x20;

const POS_Q = 32;
const TAU = Math.PI * 2;

export interface OwnerState {
  id: number;
  /** Sender clock in ms (mod 2^32). */
  time: number;
  x: number;
  z: number;
  yaw: number;
  speed: number;
  height: number;
  hpPct: number;
  mpPct: number;
  flags: number;
}

export const OWNER_ALIVE = 1;
export const OWNER_DOWN = 2;
export const OWNER_AIR = 4;
export const OWNER_INVULN = 8;

export interface MonsterState {
  id: number;
  x: number;
  z: number;
  yaw: number;
  speed: number;
  height: number;
  hp: number;
  flags: number;
  /** Stagger bar 0..255 (255 = full); only meaningful when FLAG_STAGGER_BAR. */
  stagger: number;
}

export const MON_ALIVE = 1;
export const MON_STAGGER_ACTIVE = 2;
export const MON_COUNTERED = 4;
export const MON_HAS_STAGGER = 8;
export const MON_DOWN = 16;
export const MON_AIR = 32;

const qPos = (v: number) => Math.max(-32768, Math.min(32767, Math.round(v * POS_Q)));
const qYaw = (a: number) => Math.round((((a % TAU) + TAU) % TAU) / TAU * 255) & 255;
const dYaw = (b: number) => {
  const a = (b / 255) * TAU;
  return a > Math.PI ? a - TAU : a;
};
const clamp8 = (v: number) => Math.max(0, Math.min(255, Math.round(v)));

export function encodeOwner(s: OwnerState): Uint8Array {
  const buf = new ArrayBuffer(21);
  const d = new DataView(buf);
  d.setUint8(0, MSG_OWNER);
  d.setUint32(1, s.id >>> 0, true);
  d.setUint32(5, s.time >>> 0, true);
  d.setInt16(9, qPos(s.x), true);
  d.setInt16(11, qPos(s.z), true);
  d.setUint8(13, qYaw(s.yaw));
  d.setUint8(14, clamp8(s.speed * 20));
  d.setUint8(15, clamp8(s.height * 50));
  d.setUint8(16, clamp8(s.hpPct * 255));
  d.setUint8(17, clamp8(s.mpPct * 255));
  d.setUint8(18, s.flags & 255);
  d.setUint16(19, 0, true);
  return new Uint8Array(buf);
}

export function decodeOwner(b: Uint8Array): OwnerState | null {
  if (b.byteLength < 21 || b[0] !== MSG_OWNER) return null;
  const d = new DataView(b.buffer, b.byteOffset, b.byteLength);
  return {
    id: d.getUint32(1, true),
    time: d.getUint32(5, true),
    x: d.getInt16(9, true) / POS_Q,
    z: d.getInt16(11, true) / POS_Q,
    yaw: dYaw(d.getUint8(13)),
    speed: d.getUint8(14) / 20,
    height: d.getUint8(15) / 50,
    hpPct: d.getUint8(16) / 255,
    mpPct: d.getUint8(17) / 255,
    flags: d.getUint8(18),
  };
}

const MON_BYTES = 18;

export function encodeMonsters(time: number, list: MonsterState[]): Uint8Array {
  const buf = new ArrayBuffer(7 + list.length * MON_BYTES);
  const d = new DataView(buf);
  d.setUint8(0, MSG_MONSTERS);
  d.setUint32(1, time >>> 0, true);
  d.setUint16(5, list.length, true);
  let o = 7;
  for (const m of list) {
    d.setUint32(o, m.id >>> 0, true);
    d.setInt16(o + 4, qPos(m.x), true);
    d.setInt16(o + 6, qPos(m.z), true);
    d.setUint8(o + 8, qYaw(m.yaw));
    d.setUint8(o + 9, clamp8(m.speed * 20));
    d.setUint8(o + 10, clamp8(m.height * 50));
    d.setUint32(o + 11, Math.max(0, Math.min(0xffffffff, Math.round(m.hp))), true);
    d.setUint8(o + 15, m.flags & 255);
    d.setUint8(o + 16, clamp8(m.stagger));
    d.setUint8(o + 17, 0);
    o += MON_BYTES;
  }
  return new Uint8Array(buf);
}

export function decodeMonsters(b: Uint8Array): { time: number; list: MonsterState[] } | null {
  if (b.byteLength < 7 || b[0] !== MSG_MONSTERS) return null;
  const d = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const time = d.getUint32(1, true);
  const n = d.getUint16(5, true);
  if (b.byteLength < 7 + n * MON_BYTES) return null;
  const list: MonsterState[] = [];
  let o = 7;
  for (let i = 0; i < n; i++) {
    list.push({
      id: d.getUint32(o, true),
      x: d.getInt16(o + 4, true) / POS_Q,
      z: d.getInt16(o + 6, true) / POS_Q,
      yaw: dYaw(d.getUint8(o + 8)),
      speed: d.getUint8(o + 9) / 20,
      height: d.getUint8(o + 10) / 50,
      hp: d.getUint32(o + 11, true),
      flags: d.getUint8(o + 15),
      stagger: d.getUint8(o + 16),
    });
    o += MON_BYTES;
  }
  return { time, list };
}

/** Max monsters per snapshot packet (keeps packets under ~1100 bytes). */
export const MONSTERS_PER_PACKET = 58;
